<template>
  <div class="page-container">
    <el-card v-loading="loading">
      <!-- 元信息栏 -->
      <div v-if="current && current.updated_at" class="consent-meta">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="创建时间">{{ current.created_at || '—' }}</el-descriptions-item>
          <el-descriptions-item label="最后更新">{{ current.updated_at || '—' }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 预览模式 -->
      <template v-if="!editing">
        <div class="section-header">
          <span class="section-title">知情书内容</span>
          <el-button
            v-permission="['consent:save']"
            type="primary"
            size="mini"
            icon="el-icon-edit"
            @click="startEdit"
          >编辑</el-button>
        </div>

        <!-- 富文本预览 -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          class="preview-body"
          v-html="current && current.content
            ? current.content
            : '<p class=\'empty-tip\'>暂无知情书内容，点击右上角「编辑」添加</p>'"
        />

        <!-- 需勾选条款预览 -->
        <div class="section-header section-header--sub">
          <span class="section-title">需勾选条款</span>
        </div>
        <template v-if="current && current.checkboxes && current.checkboxes.length">
          <div
            v-for="(item, idx) in current.checkboxes"
            :key="idx"
            class="checkbox-preview-item"
          >
            <i class="el-icon-check checkbox-icon" />
            <span>{{ item.content }}</span>
          </div>
        </template>
        <div v-else class="empty-tip-text">暂无需勾选条款</div>
      </template>

      <!-- 编辑模式 -->
      <el-form
        v-else
        ref="editForm"
        :model="editForm"
        :rules="editRules"
        label-width="100px"
        class="edit-form"
      >
        <!-- 富文本内容 -->
        <div class="section-header">
          <span class="section-title">知情书内容</span>
        </div>
        <el-form-item label="正文内容" prop="content" class="rich-form-item">
          <rich-editor
            v-model="editForm.content"
            :height="480"
            placeholder="请输入知情书正文内容"
          />
        </el-form-item>

        <!-- 需勾选条款 -->
        <div class="section-header section-header--sub">
          <span class="section-title">需勾选条款</span>
          <el-button
            type="success"
            size="mini"
            icon="el-icon-plus"
            @click="addCheckbox"
          >添加条款</el-button>
        </div>

        <div v-if="editForm.checkboxes.length === 0" class="empty-tip-text">
          暂无条款，点击右侧「添加条款」按钮新增
        </div>

        <div
          v-for="(item, idx) in editForm.checkboxes"
          :key="idx"
          class="checkbox-edit-row"
        >
          <span class="checkbox-index">{{ idx + 1 }}</span>
          <el-form-item
            :prop="`checkboxes.${idx}.content`"
            :rules="[{ required: true, message: '条款内容不能为空', trigger: 'blur' }]"
            class="checkbox-form-item"
          >
            <el-input
              v-model="item.content"
              placeholder="请输入条款文字"
              clearable
            />
          </el-form-item>
          <el-button
            type="danger"
            size="mini"
            icon="el-icon-delete"
            circle
            class="checkbox-del-btn"
            @click="removeCheckbox(idx)"
          />
        </div>

        <!-- 操作按钮 -->
        <el-form-item class="form-actions">
          <el-button size="small" @click="cancelEdit">取消</el-button>
          <el-button
            type="primary"
            size="small"
            :loading="saving"
            icon="el-icon-check"
            @click="handleSave"
          >保存</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { getConsentForm, saveConsentForm } from '@/api/consent-form'
import RichEditor from '@/components/RichEditor/index.vue'

export default {
  name: 'ConsentFormManage',
  components: { RichEditor },
  data() {
    return {
      loading: false,
      saving: false,
      editing: false,
      current: null,
      editForm: {
        content: '',
        checkboxes: []
      },
      editRules: {
        content: [{ required: true, message: '知情书正文内容不能为空', trigger: 'blur' }]
      }
    }
  },
  created() {
    this.fetchDetail()
  },
  activated() {
    this.fetchDetail()
  },
  methods: {
    async fetchDetail() {
      this.loading = true
      try {
        const res = await getConsentForm()
        this.current = res.data || {}
      } finally {
        this.loading = false
      }
    },

    startEdit() {
      this.editForm = {
        content: (this.current && this.current.content) || '',
        checkboxes: (this.current && this.current.checkboxes)
          ? this.current.checkboxes.map(item => ({ content: item.content || '' }))
          : []
      }
      this.editing = true
    },

    cancelEdit() {
      this.editing = false
      this.$refs.editForm && this.$refs.editForm.clearValidate()
    },

    addCheckbox() {
      this.editForm.checkboxes.push({ content: '' })
    },

    removeCheckbox(idx) {
      this.editForm.checkboxes.splice(idx, 1)
    },

    handleSave() {
      this.$refs.editForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const res = await saveConsentForm({
            content: this.editForm.content,
            checkboxes: this.editForm.checkboxes.map(item => ({ content: item.content }))
          })
          this.$message.success('保存成功')
          this.current = res.data
          this.editing = false
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.consent-meta {
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;

  &--sub {
    margin-top: 28px;
  }

  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    position: relative;
    padding-left: 10px;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 16px;
      background: #409eff;
      border-radius: 2px;
    }
  }
}

.preview-body {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 24px 28px;
  min-height: 200px;
  background: #fafafa;
  line-height: 1.8;
  color: #303133;
  font-size: 14px;
  overflow: auto;

  ::v-deep .empty-tip { color: #c0c4cc; }
  ::v-deep h1, ::v-deep h2, ::v-deep h3 { margin: 14px 0 8px; font-weight: 600; }
  ::v-deep p  { margin: 0 0 10px; }
  ::v-deep ul, ::v-deep ol { padding-left: 20px; margin-bottom: 10px; }
  ::v-deep a  { color: #409eff; }
  ::v-deep img { max-width: 100%; height: auto; border-radius: 2px; }
}

.checkbox-preview-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 16px;
  margin-bottom: 8px;
  background: #f0f9ff;
  border: 1px solid #b3d8ff;
  border-radius: 6px;
  font-size: 14px;
  color: #303133;
  line-height: 1.6;

  .checkbox-icon {
    color: #409eff;
    font-size: 16px;
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.empty-tip-text {
  color: #c0c4cc;
  font-size: 13px;
  padding: 12px 0;
  text-align: center;
}

.edit-form {
  margin-top: 4px;

  ::v-deep .el-form-item__content {
    line-height: normal;
  }
}

.rich-form-item {
  ::v-deep .el-form-item__label {
    line-height: 40px;
  }
}

.checkbox-edit-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;

  .checkbox-index {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #409eff;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .checkbox-form-item {
    flex: 1;
    margin-bottom: 0;

    ::v-deep .el-form-item__content {
      line-height: normal;
    }
  }

  .checkbox-del-btn {
    flex-shrink: 0;
  }
}

.form-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e4e7ed;
  text-align: right;

  ::v-deep .el-form-item__content {
    margin-left: 0 !important;
  }
}
</style>
